import type { Ctx } from '../../types'
import type { MyWebcamStatePayload } from '../../types/mfc'
import {
    BROADCASTER_CONTAINER_ID,
    BROADCASTER_WRAPPER_ID,
    VIDEO_SERVER_REQ_ENDPOINT,
} from '../constants/myWebcam'

export type ModelDirectorResponse = {
    videoserver: string
}

export type EventDispatcher = (data: MyWebcamStatePayload) => void

export type VideoPublisherEvent =
    | 'broadcasting'
    | 'connected'
    | 'devices'
    | 'error'
    | 'iceCandidateReceived'
    | 'miclevel'
    | 'resolutionwarning'
    | 'stats'
    | 'streamtracks'

export const VIDEO_PUBLISHER_EVENTS = [
    'broadcasting',
    'connected',
    'devices',
    'error',
    'iceCandidateReceived',
    'miclevel',
    'resolutionwarning',
    'stats',
    'streamtracks',
] as const

export const checkIfWebcamIsAvailable = async () => {
    const mediaDevices = window?.navigator?.mediaDevices
    if (mediaDevices && mediaDevices.enumerateDevices) {
        const devices = await mediaDevices.enumerateDevices()
        const cameras = devices.filter((device) => device.kind === 'videoinput')
        if (cameras.length > 0) {
            return true
        }
    }
    return false
}

export const getVideoServerRequestURL = (userId: number) =>
    `${VIDEO_SERVER_REQ_ENDPOINT}/?userid=${userId}&c2c=1&nc=${Math.random()}`

export const getVideoWrapper = (): HTMLDivElement =>
    document.querySelector(`#${BROADCASTER_WRAPPER_ID}`)

export const getVideoContainer = (): HTMLDivElement =>
    document.querySelector(`#${BROADCASTER_CONTAINER_ID}`)

export const getVideoElement = (videoContainer: HTMLDivElement) =>
    videoContainer.getElementsByTagName('video')?.[0]

const dispatchNormalizedEvent =
    (
        eventType: VideoPublisherEvent,
        eventDispatcher: EventDispatcher,
    ): EventDispatcher =>
    (data) =>
        eventType === 'connected' || eventType === 'stats'
            ? eventDispatcher(data)
            : eventDispatcher({ [eventType]: data })

const onEvents = (eventDispatcher: EventDispatcher) =>
    VIDEO_PUBLISHER_EVENTS.reduce(
        (acc, eventType) => {
            acc[eventType] = dispatchNormalizedEvent(eventType, eventDispatcher)
            return acc
        },
        {} as Record<VideoPublisherEvent, EventDispatcher>,
    )

// const onEvents = (eventDispatcher: EventDispatcher) => ({
//     broadcasting: dispatchNormalizedEvent('broadcasting', eventDispatcher),
//     connected: dispatchNormalizedEvent('connected', eventDispatcher),
//     devices: dispatchNormalizedEvent('devices', eventDispatcher),
//     error: dispatchNormalizedEvent('error', eventDispatcher),
//     iceCandidateReceived: dispatchNormalizedEvent(
//         'iceCandidateReceived',
//         eventDispatcher,
//     ),
//     miclevel: dispatchNormalizedEvent('miclevel', eventDispatcher),
//     resolutionwarning: dispatchNormalizedEvent(
//         'resolutionwarning',
//         eventDispatcher,
//     ),
//     stats: dispatchNormalizedEvent('stats', eventDispatcher),
//     streamtracks: dispatchNormalizedEvent('streamtracks', eventDispatcher),
// })

export interface InitVideoPublisherProps {
    containerVideoElement: HTMLDivElement
    sessionId: number
    userId: number
    ctx: Ctx
    tkx: string
    vidserv: string
    eventDispatcher: EventDispatcher
}

export const initVideoPublisher = (props: InitVideoPublisherProps) => {
    if (typeof window === 'undefined') return
    const {
        eventDispatcher = () => {},
        containerVideoElement,
        ctx,
        sessionId,
        tkx,
        userId,
        vidserv,
    } = props

    const publisherOptions = {
        audioBitrate: 96,
        c2c: true,
        camscore: 0,
        canvasPlugin: {},
        clbSession: 0,
        // canvasPlugin://VideoPlayerCanvasPlugin object for video overlays,
        connectingMsgExtra: '',
        // userId is whats used for room id in MyWebcam streams
        constraints: {
            audio: {
                channelCount: 2,
                echoCancellation: false,
            },
            video: {
                aspectRatio: 16 / 9,
                height: 360,
                width: 640,
            },
        },
        container: BROADCASTER_CONTAINER_ID,
        containerEl: containerVideoElement,
        // NOTE_2021_03_04 (Jose Camporro):
        // Make sure to disable this after dev is done
        disableAuth: false,
        disableReconnect: false,
        events: onEvents(eventDispatcher),
        fcs: window.FCS,
        grpSession: 0,
        host: `${vidserv}.myfreecams.com`,
        imageUrl: 'https://assets.mfcimg.com',
        // logFn: log,
        mode: '',
        modelId: userId,
        password: tkx,
        platformId: 1,
        preferredAudioDeviceLabel: '',
        preferredVideoDeviceLabel: '',
        pvtSession: 0,
        roomId: userId,
        sessionId: sessionId,
        streamName: '',
        vidctx: encodeURIComponent(JSON.stringify(ctx)),
        videoBitrate: 500,
        videoFrameRate: 30,
    }

    window.__MFC_VIDEO_PUBLISHER__ = new window.VideoPublisher(publisherOptions)
}

export interface InitMyWebcamProps {
    sessionId: number
    userId: number
    ctx: Ctx
    tkx: string
    vidserv: string
    eventDispatcher: EventDispatcher
}

export const initMyWebcam = async (props: InitMyWebcamProps) => {
    const { sessionId, userId, ctx, tkx, vidserv, eventDispatcher } = props
    const containerVideoWrapper = getVideoWrapper()
    const containerVideoElement = getVideoContainer()
    const showVideo = (show: boolean) => {
        containerVideoElement.style.display = show ? 'flex' : 'none'
    }
    initVideoPublisher({
        containerVideoElement,
        sessionId,
        userId,
        ctx,
        tkx,
        vidserv,
        eventDispatcher,
    })
    const isReady = !!window?.__MFC_VIDEO_PUBLISHER__?.connect
    const isWebcamAvailable = await checkIfWebcamIsAvailable()
    if (isReady) {
        eventDispatcher({
            isVideoPublisherReady: isReady,
            isWebcamAvailable,
            containerVideoWrapper,
            containerVideoElement,
            publisher: window.__MFC_VIDEO_PUBLISHER__,
            showVideo,
        })
    }
}
